// React
import React from "react";

// Material-UI (Core)
import { Box, IconButton, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

// Libraries
import { useObserver } from "mobx-react-lite";
import styled from "styled-components";

// Core
import { StoresInstance } from "Core/Base";

// Custom
import { Client } from "Custom/Globals/AppUrls";
import { getHistory } from "Core/Utils/Utils";

const BAR_HEIGHT = 31;

const NotificationContainer = styled(Box)`
    align-items: center;
    background: #d0e6c7;
    border-bottom: 1px solid #d0e6c7;
    color: #3c3c3b;
    cursor: pointer;
    display: flex;
    gap: 16px;
    justify-content: center;
    left: 0;
    height: ${BAR_HEIGHT}px;
    min-height: ${BAR_HEIGHT}px;
    max-height: ${BAR_HEIGHT}px;
    padding: 8px 16px;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1300;

    &:hover {
        background: #bbdea2;
    }
`;

const NotificationText = styled(Typography)`
    color: #3c3c3b;
    flex: 1;
    font: normal normal bold 12px/18px Inter;
    letter-spacing: -0.18px;
    text-align: center;
`;

const CloseButton = styled(IconButton)`
    color: #3c3c3b !important;
    flex: 0 0 auto;
    padding: 6px !important;
    background: #449b23 0% 0% no-repeat padding-box !important;
    border-radius: 0 !important;

    &:hover {
        background-color: rgba(60, 60, 59, 0.08) !important;
    }
`;

export const getSuperAdminBarHeight = (): number => {
    const superAdminStore = StoresInstance.domain.SuperAdminStore;
    const accountStore = StoresInstance.domain.AccountStore;
    if (!accountStore.IsLoggedIn) {
        return 0;
    }

    return superAdminStore.selectedCustomer && superAdminStore.isNotificationVisible ? BAR_HEIGHT : 0;
};

export const SuperAdminNotificationBar: React.FC = () =>
    useObserver(() => {
        const superAdminStore = StoresInstance.domain.SuperAdminStore;
        const accountStore = StoresInstance.domain.AccountStore;
        const selectedCustomer = superAdminStore.selectedCustomer;

        if (!accountStore.IsLoggedIn || !selectedCustomer || !superAdminStore.isNotificationVisible) {
            return null;
        }

        const notificationText = `You are currently viewing Plot ${selectedCustomer.plotNumber}, ${selectedCustomer.developmentName}, ${selectedCustomer.houseTypeName} - ${selectedCustomer.customerName}`;

        const handleNotificationClick = () => {
            superAdminStore.clearSelectedCustomer();
            getHistory().push(Client.Path.SuperAdmin);
        };

        const handleCloseClick = (event: React.MouseEvent) => {
            event.stopPropagation();
            superAdminStore.hideNotificationForSession();
        };

        return (
            <NotificationContainer onClick={handleNotificationClick} role="button" aria-label="Return to Super Admin view">
                <NotificationText variant="body2">{notificationText}</NotificationText>
                <CloseButton onClick={handleCloseClick} size="small" aria-label="Hide notification">
                    <CloseIcon style={{ fontSize: 18 }} />
                </CloseButton>
            </NotificationContainer>
        );
    });
